import {
  getEnvironmentVariables
} from "./general";
import type {
  LearningActivity,
  UserData
} from "../types";

const {
  scormTracker,
  xapiTracker
} = getEnvironmentVariables();

// Cache-busting dello script della libreria: valore non sensibile, rinnovato a ogni
// caricamento pagina (prima si usava ?hash=<sessionToken>, che esponeva la sessione).
const TRACKER_SCRIPT_VERSION = Date.now();

export function getTrackerScriptUrl(scriptUrl: string) {
  return `${scriptUrl}?v=${TRACKER_SCRIPT_VERSION}`;
}

// Riproduce il valore che produceva l'interpolazione nel vecchio script inline
// (es. courseId: ${"123"} -> 123, ${"null"} -> null, ${undefined} -> undefined).
function toLibNumber(value?: string) {
  if (value === undefined) return undefined;
  if (value === "null") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export type ScormTrackerLibInput = ReturnType<typeof getScormTrackerLibInput>;

// libInput passato come oggetto al costruttore di TrackerDataInput, non più come
// testo di uno <script> inline: sessionId e tokenJwt non finiscono nel DOM.
export function getScormTrackerLibInput({
  accessToken,
  detailsPath,
  learningObject,
  sessionToken,
  userData,
  calculatedCourseId,
  calculatedRootId,
  language
} : {
  accessToken: string
  detailsPath: string
  learningObject: LearningActivity
  sessionToken: string
  userData: UserData,
  calculatedCourseId?: string,
  calculatedRootId?: string,
  language: string | undefined
}) {
  return {
    activityId: learningObject?.id,
    corporateId: userData?.organization_id,
    courseId: toLibNumber(calculatedCourseId),
    domain: `${window.location.origin}/${detailsPath}`,
    iframeSrc: String(learningObject?.link),
    initiativeId: userData?.initiative_id,
    isLinkRelative: learningObject?.isLinkRelative,
    langCode: String(language),
    learnerId: userData?.id_user,
    learnerName: `${userData?.name ?? ""} ${userData?.surname ?? ""}`,
    LO_type: String(learningObject?.learningObjectTypology),
    lti_account_id: String(learningObject?.lti_account_id?.id || null),
    objectDescription: learningObject.shortDescription ?? "",
    objectName: learningObject.title ?? "",
    pathId: toLibNumber(calculatedRootId),
    sessionId: sessionToken,
    standaloneActivity: learningObject?.isStandAlone,
    standaloneCourse: learningObject?.courseDetails?.isStandAlone,
    status: String(learningObject?.status),
    surveyId: learningObject.surveyId,
    tentativeId: learningObject?.tentativeId,
    tojEditionId: learningObject?.tojEditionIds?.[0],
    tokenJwt: accessToken,
    trackOnClick: learningObject?.trackOnClick,
    userEmail: String(userData?.email),
    userName: String(userData?.external_id),
    VITE_SCORM_TRACKER: scormTracker,
    VITE_XAPI_TRACKER: xapiTracker
  };
}

// Da chiamare nell'onload dello script della libreria (getTrackerScriptUrl).
export function startScormTracker(libInput: ScormTrackerLibInput) {
  const TrackerDataInput = window.TrackerDataInput;
  if (!TrackerDataInput) {
    // eslint-disable-next-line no-console
    console.error("startScormTracker: TrackerDataInput non disponibile");
    return;
  }
  try {
    new TrackerDataInput(libInput).start();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("startScormTracker error: ", error);
  }
}
